import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  getSavedPaletteId,
  savePaletteId,
  getPalette,
  DEFAULT_LIGHT_PALETTE,
  DEFAULT_DARK_PALETTE,
} from '../theme/palettes';
import { useTheme } from './useTheme';

const PaletteContext = createContext(null);

/**
 * Wrap your app with <PaletteProvider> (inside <ThemeProvider>) to share
 * graph-palette state across the entire component tree.
 *
 * Returned context shape:
 *   { paletteId, effectivePaletteId, palette, setPaletteById }
 *
 * - `paletteId`          — the user's last explicitly-chosen standalone palette
 * - `effectivePaletteId` — the palette actually in use (may be overridden by a linked theme)
 * - `palette`            — the flattened color object ready for Chart.js
 * - `setPaletteById(id)` — select a standalone palette (persists to localStorage)
 */
export function PaletteProvider({ children }) {
  const { isDark, linkedPaletteId } = useTheme();

  const [paletteId, setPaletteId] = useState(() => {
    const fallback = isDark ? DEFAULT_DARK_PALETTE : DEFAULT_LIGHT_PALETTE;
    return getSavedPaletteId(fallback);
  });

  // Track the user's standalone palette so we can restore it after a linked
  // theme override ends.  Initialised from localStorage (the last explicit choice).
  const preLinkPaletteRef = useRef(paletteId);

  // When the active theme has a linked palette, stash the current standalone
  // palette and switch to the linked one.  When the linked palette goes away,
  // restore the previously-stashed standalone palette.
  useEffect(() => {
    if (linkedPaletteId) {
      // Only stash if we aren't already overridden (avoids re-stashing the
      // linked palette itself on subsequent renders).
      if (preLinkPaletteRef.current !== linkedPaletteId) {
        preLinkPaletteRef.current = paletteId;
      }
      setPaletteId(linkedPaletteId);
    } else if (preLinkPaletteRef.current) {
      // Restore the stashed standalone palette
      const restored = preLinkPaletteRef.current;
      setPaletteId(restored);
      savePaletteId(restored);
    }
  }, [linkedPaletteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const effectivePaletteId = linkedPaletteId || paletteId;
  const palette = getPalette(effectivePaletteId);

  const setPaletteById = useCallback((id) => {
    setPaletteId(id);
    savePaletteId(id);
  }, []);

  return (
    <PaletteContext.Provider
      value={{ paletteId, effectivePaletteId, palette, setPaletteById }}
    >
      {children}
    </PaletteContext.Provider>
  );
}

/**
 * Consume the shared palette state.
 * Must be used inside <PaletteProvider>.
 *
 * Returns: { paletteId, effectivePaletteId, palette, setPaletteById }
 */
export function usePalette() {
  const ctx = useContext(PaletteContext);
  if (!ctx) throw new Error('usePalette must be used inside <PaletteProvider>');
  return ctx;
}
